import type {
  AccordionFragment,
  EmbedAccordionGroupFragment,
  EmbedBynderAssetFragment,
  EmbedComponentFragment,
  EmbedCustomContentFragment,
  RichTextAccordionFragment,
} from '@seek/cmsu-cms-connect';
import { AccordionGroup } from '@seek/cmsu-components';
import { renderers } from '@seek/cmsu-rich-text';
import { Badge, Box, ContentBlock, Divider } from 'braid-design-system';
import { breakpoints, vars } from 'braid-design-system/css';
import React from 'react';

import type { ArticleAdditionalData } from 'src/client/pages/Article';
import type { AssetBynder } from 'src/client/shared/types';

import { CustomContent } from '../../CustomContent/CustomContent';

export const toAccordions = (
  accordions: RichTextAccordionFragment[],
): AccordionFragment[] =>
  accordions.map((accordion) => ({
    id: accordion.id,
    label: accordion.label,
    content: accordion.content,
    locale: accordion.locale,
  }));

export const richTextRenderers = (additionalData?: ArticleAdditionalData) => ({
  ...renderers,
  embed: {
    EmbedBynderAsset: ({
      asset,
    }: EmbedBynderAssetFragment & { asset: AssetBynder }) => (
      <Box>
        <img
          src={asset.files.webImage.url}
          srcSet={generateBynderSrcset(asset)}
          sizes={`(max-width: ${breakpoints.tablet}px) 100vw, 60vw`}
          alt={asset.name}
          style={{
            width: '100%',
            paddingTop: vars.space.small,
            paddingBottom: vars.space.small,
          }}
        />
      </Box>
    ),
    EmbedAccordionGroup: ({ accordion }: EmbedAccordionGroupFragment) => (
      <Box background="surface" paddingX="gutter" paddingY="gutter">
        <ContentBlock>
          <AccordionGroup accordions={toAccordions(accordion)} />
        </ContentBlock>
      </Box>
    ),
    EmbedCustomContent: ({ title }: EmbedCustomContentFragment) => (
      <CustomContent title={title} additionalData={additionalData} />
    ),
    EmbedComponent: ({ component }: EmbedComponentFragment) =>
      embedComponentRenderers(component),
  },
});

const embedComponentRenderers = (
  component?: EmbedComponentFragment['component'],
) => {
  if (!component) return <React.Fragment />;

  switch (component.__typename) {
    case 'LineDivider':
      return <Divider weight={component.weight || 'regular'} />;
    case 'Badge':
      return <Badge tone={component.tone}>{component.text}</Badge>;
    default:
      return <React.Fragment />;
  }
};

/**
 * Generates responsive image srcset for Bynder assets
 * Provides 2 image sizes: thumbnail for mobile/tablet, webImage for desktop+
 * byder provides 3 sizes: mini, thumbnail, webImage
 * mini = 80px width (won't be using mini)
 * thumbnail = 250px width
 * webImage = 800px width
 */
const generateBynderSrcset = (asset: AssetBynder) => {
  const sizes = [
    { size: 'thumbnail', maxWidth: breakpoints.tablet },
    { size: 'webImage', maxWidth: breakpoints.wide },
  ];

  return sizes
    .map(({ size, maxWidth }) => {
      const file = asset.files[size as keyof typeof asset.files];
      return `${file.url} ${maxWidth}w`;
    })
    .join(', ');
};
